import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { StuckButton } from "@/components/StuckButton";
import { celebrate } from "@/lib/confetti";
import {
  GuideOverlay, ProgressTracker, MissionStepper, FirstClientCard, PreviousBusinesses,
} from "@/components/SpaceGuide";
import {
  AlertDialog, AlertDialogContent,
  AlertDialogDescription, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PLAN_NAMES = { depart: "Départ", lancement: "Lancement", acceleration: "Accélération" };

export default function Account() {
  const navigate = useNavigate();
  const { user, isAuthed, logout, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [results, setResults] = useState([]);
  const [sub, setSub] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pending, setPending] = useState(null);
  const [bizState, setBizState] = useState(null);

  // Guided client space
  const [space, setSpace] = useState(null);
  const [spaceLoaded, setSpaceLoaded] = useState(false);
  const [spaceError, setSpaceError] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const loadSpace = useCallback(async () => {
    try {
      const { data } = await api.get("/space");
      setSpace(data);
      setSpaceError(false);
      setSpaceLoaded(true);
      if (data.has_business && !data.progress.onboarding_seen) setGuideOpen(true);
    } catch (e) {
      setSpaceError(true);
      setSpaceLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/credits/me").then(({ data }) => setInfo(data)).catch(() => {});
    api.get("/auth/results").then(({ data }) => setResults(data.results)).catch(() => {});
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/auth/pending").then(({ data }) => setPending(data.pending)).catch(() => {});
    loadSpace();
  }, [isAuthed, loadSpace]);

  useEffect(() => {
    if (!isAuthed) return;
    api.post("/auth/track-access").catch(() => {});
    const t = setInterval(() => { api.post("/auth/track-access").catch(() => {}); }, 20000);
    return () => clearInterval(t);
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed || sub === null || bizState) return;
    const hasBusiness = results.some((r) => r.business_name);
    if (!sub?.has_subscription || hasBusiness) return;
    api.post("/auth/ensure-business").then(({ data }) => {
      setBizState(data.status);
      if (data.status === "generating") {
        const t = setInterval(async () => {
          try {
            const { data: rd } = await api.get("/auth/results");
            if (rd.results.some((r) => r.business_name)) { setResults(rd.results); setBizState("ready"); clearInterval(t); loadSpace(); }
          } catch (e) {}
        }, 5000);
        setTimeout(() => clearInterval(t), 120000);
      }
    }).catch(() => {});
  }, [isAuthed, sub, results, bizState, loadSpace]);

  const startGuide = async () => {
    setGuideOpen(false);
    try { await api.post("/space/onboarding-seen"); } catch (e) {}
    setSpace((s) => (s ? { ...s, progress: { ...s.progress, onboarding_seen: true } } : s));
    if (space?.result_id) navigate(`/result/${space.result_id}`);
  };
  const closeGuide = async () => {
    setGuideOpen(false);
    try { await api.post("/space/onboarding-seen"); } catch (e) {}
    setSpace((s) => (s ? { ...s, progress: { ...s.progress, onboarding_seen: true } } : s));
  };

  const applyProgress = (d) => {
    setSpace((s) => s ? {
      ...s,
      steps: d.steps || s.steps,
      percent: d.percent != null ? d.percent : s.percent,
      next_action: d.next_action || s.next_action,
      progress: { ...s.progress, day2_unlocked: d.day2_unlocked != null ? d.day2_unlocked : s.progress.day2_unlocked },
    } : s);
  };

  const toggleAction = async (actionId, done) => {
    setSpace((s) => s ? { ...s, progress: { ...s.progress, actions_done: { ...s.progress.actions_done, ...(done ? { [actionId]: true } : {}) } } } : s);
    if (!done) {
      setSpace((s) => { if (!s) return s; const nd = { ...s.progress.actions_done }; delete nd[actionId]; return { ...s, progress: { ...s.progress, actions_done: nd } }; });
    }
    try {
      const { data } = await api.post("/space/action", { action_id: actionId, done });
      applyProgress(data);
    } catch (e) { toast.error("Impossible d'enregistrer, réessaie."); }
  };

  const markFirstClient = async () => {
    celebrate();
    try {
      const { data } = await api.post("/space/first-client");
      setSpace((s) => s ? { ...s, progress: { ...s.progress, first_client: true } } : s);
      applyProgress(data);
      toast.success("Félicitations pour ton premier client ! 🎉");
    } catch (e) { toast.error("Impossible d'enregistrer, réessaie."); }
  };

  const goNext = () => {
    const t = space?.next_action?.target;
    if (t === "business") { if (space?.result_id) navigate(`/result/${space.result_id}`); return; }
    const map = { day1: "mission-day1", day2: "mission-day2", client: "first-client" };
    const el = document.getElementById(map[t]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (user === null) return <div className="center-page" role="status"><p className="eyebrow">Chargement...</p></div>;

  if (!isAuthed) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container page-content fade-in lookup-layout">
          <div><p className="eyebrow">TON COMPTE</p><h1>Connecte-toi.</h1><p className="muted mt-6">Retrouve ton business, ton plan et tes crédits.</p></div>
          <div className="lookup-form">
            <button onClick={() => navigate("/retrouver")} data-testid="account-email-login" className="btn-primary w-full">Me connecter par email</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const balance = info?.credits_balance ?? user.credits_balance ?? 0;
  const monthly = info?.credits_monthly ?? user.credits_monthly ?? 0;
  const pct = monthly ? Math.min(100, Math.round((balance / monthly) * 100)) : 0;
  const planName = PLAN_NAMES[info?.plan || user.plan] || "Aucun abonnement";
  const hasSub = sub?.has_subscription;
  const canceled = sub?.cancel_at_period_end;
  const renewalDate = sub?.renewal_date || info?.renewal_date;
  const renewalStr = renewalDate ? new Date(renewalDate).toLocaleDateString("fr-FR") : null;
  const hasBiz = results.some((r) => r.business_name);
  const spaceReady = space && space.has_business;
  const missions = space?.missions || {};

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      setSub((s) => ({ ...(s || {}), cancel_at_period_end: true, status: data.status, renewal_date: data.renewal_date, has_subscription: true }));
      toast.success(`Résiliation enregistrée. Ton accès reste actif jusqu'au ${data.renewal_date ? new Date(data.renewal_date).toLocaleDateString("fr-FR") : "terme de la période"}.`);
      if (refresh) refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La résiliation a échoué. Réessaie.");
    } finally {
      setCancelling(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="min-h-screen has-stuck-fab">
      <Navbar />
      {guideOpen && spaceReady && (
        <GuideOverlay firstName={space.first_name} onStart={startGuide} onClose={closeGuide} />
      )}
      <main className="container page-content fade-in">
        <p className="eyebrow">TON COMPTE</p>
        <h1>Bonjour, <em>{user.name || user.email}</em></h1>

        {pending && (
          <PendingReservation pending={pending} onExpire={() => setPending(null)} onResume={() => navigate(`/result/${pending.result_id}?paying=1`)} />
        )}

        {hasSub && !hasBiz && bizState === "generating" && (
          <div className="pending-reservation mt-8" data-testid="account-generating">
            <div className="pending-res-glow" aria-hidden="true" />
            <div className="pending-res-left">
              <p className="field-label">Ton accès est actif ✅</p>
              <p className="pending-res-name">Ton business est en cours de création…</p>
              <p className="muted text-sm mt-1">On prépare ton idée et ton plan d'action sur-mesure. Cette page se met à jour automatiquement.</p>
              <div className="load40-bar mt-4" style={{ maxWidth: 260 }}><div className="load40-bar-fill" style={{ width: "60%" }} /></div>
            </div>
          </div>
        )}
        {hasSub && !hasBiz && bizState !== "generating" && (
          <div className="pending-reservation mt-8" data-testid="account-generate-cta">
            <div className="pending-res-glow" aria-hidden="true" />
            <div className="pending-res-left">
              <p className="field-label">Ton accès est actif ✅</p>
              <p className="pending-res-name">Crée ton business en 2 minutes</p>
              <p className="muted text-sm mt-1">Réponds au quiz rapide pour générer ton business et ton plan d'action sur-mesure.</p>
            </div>
            <button onClick={() => navigate("/quiz")} data-testid="account-generate-quiz" className="btn-primary pending-res-btn">Répondre au quiz <span aria-hidden="true">→</span></button>
          </div>
        )}

        {/* ---------- Guided client space ---------- */}
        {spaceReady && (
          <div className="space-zone mt-8" data-testid="space-zone">
            <ProgressTracker
              steps={space.steps}
              percent={space.percent}
              nextAction={space.next_action}
              onNext={goNext}
              onReplayGuide={() => setGuideOpen(true)}
            />

            <button onClick={() => navigate(`/result/${space.result_id}`)} data-testid="space-open-business" className="space-biz-cta mt-6">
              <div>
                <p className="field-label">Étape 1 — Découvre ton business</p>
                <p className="space-biz-name">{space.business_name}</p>
                <p className="muted text-sm mt-1">Ouvre ton business, ton plan 30 jours et ton script premier client.</p>
              </div>
              <span className="accent" aria-hidden="true">→</span>
            </button>

            <div className="missions-wrap mt-8">
              <MissionStepper
                dayKey="day1" anchorId="mission-day1" mission={missions.day1}
                done={space.progress.actions_done || {}} locked={false}
                onToggle={toggleAction} resultId={space.result_id}
              />
              <MissionStepper
                dayKey="day2" anchorId="mission-day2" mission={missions.day2}
                done={space.progress.actions_done || {}} locked={!space.progress.day2_unlocked}
                onToggle={toggleAction} resultId={space.result_id}
              />
            </div>

            <div className="mt-8">
              <FirstClientCard anchorId="first-client" done={space.progress.first_client} onDone={markFirstClient} />
            </div>

            <div className="mt-8">
              <PreviousBusinesses items={space.previous_businesses} />
            </div>
          </div>
        )}

        {spaceError && hasBiz && (
          <div className="space-error mt-8" data-testid="space-error">
            <p>Impossible de charger ton espace guidé.</p>
            <button onClick={loadSpace} className="btn-secondary btn-small mt-3" data-testid="space-retry">Réessayer</button>
          </div>
        )}

        <div className="account-panels mt-10">
          <div className="account-box" data-testid="account-plan">
            <p className="field-label">Ton abonnement</p>
            <p className="account-big">{planName}</p>
            {canceled ? (
              <div className="cancel-notice mt-3" data-testid="subscription-canceled-notice">
                <p className="text-sm">Abonnement résilié. Ton accès et tes crédits restent actifs jusqu'au {renewalStr || "terme de la période en cours"}, sans nouveau prélèvement.</p>
              </div>
            ) : (
              hasSub && renewalStr && <p className="muted text-sm mt-2">Prochain prélèvement le {renewalStr}</p>
            )}
            <div className="mt-5 flex flex-wrap gap-3">
              {canceled
                ? <button onClick={() => navigate("/tarifs")} data-testid="reactivate-subscription" className="btn-primary btn-small">Réactiver un abonnement →</button>
                : <button onClick={() => navigate("/tarifs")} className="btn-secondary btn-small">Changer de formule</button>}
            </div>
            {hasSub && !canceled && (
              <button onClick={() => setConfirmOpen(true)} data-testid="cancel-subscription" className="account-cancel">Résilier mon abonnement</button>
            )}
          </div>
          <div className="account-box" data-testid="account-credits">
            <p className="field-label">Tes crédits</p>
            <p className="account-big">{balance}<span className="text-base text-[color:var(--ink-2)]"> / {monthly}</span></p>
            <div className="load40-bar mt-4" style={{ maxWidth: "none" }}><div className="load40-bar-fill" style={{ width: `${pct}%` }} /></div>
            <button onClick={() => navigate("/credits")} className="btn-secondary btn-small mt-5">Voir l'historique</button>
          </div>
        </div>

        <button onClick={() => navigate("/coach")} data-testid="account-coach-cta" className="account-coach-cta mt-8">
          <div><p className="field-label">Étape 3 — Coach IA</p><p className="text-sm mt-1">Pose tes questions à ton coach business, adapté à ton idée et ton plan.</p></div>
          <span className="accent" aria-hidden="true">→</span>
        </button>

        <div className="rule mt-14 pt-10">
          <p className="field-label">Tes business</p>
          <div className="mt-6">
            {results.length === 0 && <p className="muted text-sm">Aucun business déverrouillé pour le moment. <button onClick={() => navigate("/quiz")} className="accent underline">En créer un</button>.</p>}
            {results.map((r) => <button key={r.id} onClick={() => navigate(`/result/${r.id}`)} data-testid={`account-result-${r.id}`} className="find-result"><div><p className="font-heading">{r.business_name}</p><p className="mono muted text-[10px] mt-2">{r.type === "physique" ? "PHYSIQUE" : "EN LIGNE"} · {new Date(r.created_at).toLocaleDateString("fr-FR")}</p></div><span className="accent" aria-hidden="true">→</span></button>)}
          </div>
        </div>

        <button onClick={logout} data-testid="logout-button" className="btn-secondary btn-small mt-12">Se déconnecter</button>
      </main>

      {spaceReady && <StuckButton context="mon espace" />}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="retention-dialog" className="retention-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="retention-title">Tu es sur le point de perdre :</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Ce que tu perds en résiliant ton abonnement.</AlertDialogDescription>
          </AlertDialogHeader>

          <ul className="retention-list" data-testid="retention-list">
            {sub?.journey_month > 0 && (
              <li data-testid="retention-progress">
                <span className="retention-loss-label">Ta progression</span>
                <span className="retention-loss-value">Jour {sub.journey_day}/{sub.journey_total || 90}</span>
              </li>
            )}
            <li data-testid="retention-credits">
              <span className="retention-loss-label">Tes crédits reportés</span>
              <span className="retention-loss-value">{sub?.carried_credits ?? balance} crédits</span>
            </li>
            {sub?.next_phase && (
              <li data-testid="retention-next-phase">
                <span className="retention-loss-label">La prochaine phase que tu allais débloquer</span>
                <span className="retention-loss-value">{sub.next_phase}</span>
              </li>
            )}
          </ul>

          <div className="retention-actions">
            <button onClick={() => setConfirmOpen(false)} data-testid="retention-stay" className="btn-primary w-full">Rester <span aria-hidden="true">→</span></button>
            {(info?.plan || user.plan) !== "depart" && (
              <button onClick={() => { setConfirmOpen(false); navigate("/tarifs"); }} data-testid="retention-downgrade" className="btn-secondary w-full mt-3">Passer à l'offre Départ</button>
            )}
            <button onClick={(e) => { e.preventDefault(); doCancel(); }} data-testid="retention-cancel-anyway" disabled={cancelling} className="retention-cancel-link">
              {cancelling ? "Résiliation..." : "Résilier quand même"}
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function PendingReservation({ pending, onExpire, onResume }) {
  const target = new Date(pending.reserved_until).getTime();
  const [left, setLeft] = useState(Math.max(0, Math.floor((target - Date.now()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setLeft(s);
      if (s <= 0) { clearInterval(t); onExpire && onExpire(); }
    }, 1000);
    return () => clearInterval(t);
  }, [target, onExpire]);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="pending-reservation mt-8" data-testid="account-pending-reservation">
      <div className="pending-res-glow" aria-hidden="true" />
      <div className="pending-res-left">
        <p className="field-label">Ton idée est réservée</p>
        <p className="pending-res-name" data-testid="pending-res-name">{pending.business_name || "Ton business sur-mesure"}</p>
        <p className="muted text-sm mt-1">Elle t'attend encore <b data-testid="pending-res-time">{mm}:{ss}</b> — reprends là où tu t'étais arrêté.</p>
      </div>
      <button onClick={onResume} data-testid="pending-res-resume" className="btn-primary pending-res-btn">Reprendre mon idée <span aria-hidden="true">→</span></button>
    </div>
  );
}
